import _ from 'underscore';
import Promise from 'bluebird';

function permissionService($state, $stateParams, sdkPermissionService, managementUserService, organizationService, $q) {
  const PERMISSION_TYPES = {
    READ_ORGANIZATION: 'READ_ORGANIZATION',
    WRITE_ORGANIZATION: 'WRITE_ORGANIZATION',
    MANAGE_STAFF: 'MANAGE_STAFF',
    MANAGE_CUSTOMERS: 'MANAGE_CUSTOMERS',
    EXPORT_CUSTOMERS: 'EXPORT_CUSTOMERS',
    READ_RESERVATIONS: 'READ_RESERVATIONS',
    WRITE_RESERVATIONS: 'WRITE_RESERVATIONS',
    CHECK_IN_RESERVATIONS: 'CHECK_IN_RESERVATIONS',
    READ_GUESTLISTS: 'READ_GUESTLISTS',
    WRITE_GUESTLISTS: 'WRITE_GUESTLISTS',
    CHECK_IN_GUESTLISTS: 'CHECK_IN_GUESTLISTS',
    READ_VENUE: 'READ_VENUE',
    WRITE_VENUE: 'WRITE_VENUE',
    READ_EVENTS: 'READ_EVENTS',
    WRITE_EVENTS: 'WRITE_EVENTS',
    READ_PERFORMANCE_REPORTS: 'READ_PERFORMANCE_REPORTS'
  };

  const PROMOTER_PERMISSION_TYPES = {
    READ_RESERVATIONS: 'READ_RESERVATIONS',
    WRITE_RESERVATIONS: 'WRITE_RESERVATIONS',
    READ_GUESTLISTS: 'READ_GUESTLISTS',
    WRITE_GUESTLISTS: 'WRITE_GUESTLISTS'
  };

  const PERMISSION_LEVEL = {
    SYSTEM: 'SYSTEM',
    USER: 'USER'
  };

  const PERMISSION_SCOPE = {
    ORGANIZATION: 'ORGANIZATION',
    VENUE: 'VENUE'
  };

  class PermissionService {
    constructor() {
      this._permissionTypes = []; //available
    }

    clearPermissions() {
      this._permissions = null;

      organizationService.clearCurrentOrganizationId();
    }

    get TYPE() { //eslint-disable-line
      return PERMISSION_TYPES;
    }

    /**
     * @return {Promise<Boolean>}
     */
    canReadOrganization(organizationId) {
      if (!organizationId) throw new Error('organizationId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.READ_ORGANIZATION,
        organization: organizationId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canWriteOrganization(organizationId) {
      if (!organizationId) throw new Error('organizationId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.WRITE_ORGANIZATION,
        organization: organizationId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canManageStaff(organizationId) {
      if (!organizationId) throw new Error('organizationId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.MANAGE_STAFF,
        organization: organizationId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canManageCustomers(organizationId) {
      if (!organizationId) throw new Error('organizationId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.MANAGE_CUSTOMERS,
        organization: organizationId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canExportCustomers(organizationId) {
      if (!organizationId) throw new Error('organizationId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.EXPORT_CUSTOMERS,
        organization: organizationId
      });
    }

    canReadOrWriteReservations(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return Promise.props({
          readReservation: this._hasPermission({
            value: PERMISSION_TYPES.READ_RESERVATIONS,
            venue: venueId
          }),
          writeReservation: this._hasPermission({
            value: PERMISSION_TYPES.WRITE_RESERVATIONS,
            venue: venueId
          })
        })
        .then(({
          readReservation,
          writeReservation
        }) => {
          return $q.resolve(readReservation || writeReservation);
        });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canWriteReservations(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.WRITE_RESERVATIONS,
        venue: venueId
      });
    }

    canReadOrWriteGuestlists(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return Promise.props({
          readGuestlist: this._hasPermission({
            value: PERMISSION_TYPES.READ_GUESTLISTS,
            venue: venueId
          }),
          writeGuestlist: this._hasPermission({
            value: PERMISSION_TYPES.WRITE_GUESTLISTS,
            venue: venueId
          })
        })
        .then(({
          readGuestlist,
          writeGuestlist
        }) => {
          return $q.resolve(readGuestlist || writeGuestlist);
        });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canWriteGuestlists(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.WRITE_GUESTLISTS,
        venue: venueId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canReadOrWriteVenue(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return Promise.props({
          readVenue: this._hasPermission({
            value: PERMISSION_TYPES.READ_VENUE,
            venue: venueId
          }),
          writeVenue: this._hasPermission({
            value: PERMISSION_TYPES.WRITE_VENUE,
            venue: venueId
          })
        })
        .then(({
          readVenue,
          writeVenue
        }) => {
          return $q.resolve(readVenue || writeVenue);
        });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canWriteVenue(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.WRITE_VENUE,
        venue: venueId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canReadEvents(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.READ_EVENTS,
        venue: venueId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canWriteEvents(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.WRITE_EVENTS,
        venue: venueId
      });
    }

    /**
     * @return {Promise<Boolean>}
     */
    canReadPerformanceReports(venueId) {
      if (!venueId) throw new Error('venueId is required');

      return this._hasPermission({
        value: PERMISSION_TYPES.READ_PERFORMANCE_REPORTS,
        venue: venueId
      });
    }

    setCurrentPermissions(permissions) {
      if (!permissions || !_.isArray(permissions)) throw new Error('permissions is required');

      this._permissions = permissions;
    }

    setPermissionTypes(permissionTypes) {
      if (!permissionTypes || !_.isArray(permissionTypes)) throw new Error('permissionTypes is required');

      this._permissionTypes = permissionTypes;
    }

    getVenuePermissionTypes() {
      return this._permissionTypes
        .filter(perm => {
          return perm.scope === PERMISSION_SCOPE.VENUE &&
            perm.level === PERMISSION_LEVEL.USER;
        });
    }

    getVenuePromoterPermissionTypes() {
      return this._permissionTypes
        .filter(perm => {
          return perm.scope === PERMISSION_SCOPE.VENUE && perm.level === PERMISSION_LEVEL.USER && Object.values(PROMOTER_PERMISSION_TYPES).includes(perm.value);
        });
    }

    getOrganizationPermissionTypes() {
      return this._permissionTypes
        .filter(perm => {
          return perm.scope === PERMISSION_SCOPE.ORGANIZATION &&
            perm.level === PERMISSION_LEVEL.USER;
        });
    }

    /**
     * Determine if the organization is an external organization for the current user
     *
     * @param {string} organizationId - ID of organization to check
     * @return {boolean} True if organization is external, false otherwise
     */
    isExternalOrganization(organizationId) { //eslint-disable-line
      if (!organizationId) throw new Error('organizationId is required');

      const currentUserOrganization = organizationService.getCurrentOrganization();
      if (!currentUserOrganization) return true;

      return organizationId !== currentUserOrganization.id;
    }

    _hasPermission(checks) {
      if (!checks) return $q.resolve(false);

      return this._getPermissions()
        .then(permissions => {
          this._permissions = permissions;

          const exists = _.findWhere(permissions, checks);

          return !!exists;
        });
    }

    _getPermissions() {
      const currentUser = managementUserService.getCurrentUser();

      if (!currentUser) {
        $state.go('login');

        return null;
      }

      const options = {
        organizationId: organizationService.getCurrentOrganizationId(),
        limit: 500
      };

      let promise;

      if (this._permissionsPromise) {
        promise = this._permissionsPromise;
      } else {
        promise = this._permissionsPromise = (this._permissions ? $q.resolve(this._permissions) : sdkPermissionService.listByManagementUserId(currentUser.id, options));
      }

      return promise
        .then(permissions => {
          this._permissions = permissions;

          this._permissionsPromise = null;

          return permissions;
        })
        .catch(() => {
          this._permissionsPromise = null;
        });
    }
  }

  return new PermissionService();
}

permissionService.$inject = [
  '$state',
  '$stateParams',
  'jsSdk.organizationPermission',
  'jsSdk.managementUser',
  'organizationService',
  '$q'
];

export default permissionService;



// WEBPACK FOOTER //
// ./src/services/permissionService.js